import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchSupportTickets,
  fetchSupportTicket,
  updateSupportTicket,
  type SupportTicketDto,
  type SupportTicketStatus,
  type SupportTicketPriority,
} from '../services/api'
import {
  LifeBuoy,
  ChevronRight,
  X,
  Send,
  RefreshCw,
  MessageSquare,
  AlertTriangle,
  Clock,
  CheckCircle2,
  XCircle,
} from 'lucide-react'
import './SupportTickets.css'

const STATUS_OPTIONS: SupportTicketStatus[] = ['Open', 'InProgress', 'Resolved', 'Closed']
const PRIORITY_OPTIONS: SupportTicketPriority[] = ['Low', 'Medium', 'High', 'Critical']

function statusBadge(status: SupportTicketStatus) {
  const map: Record<SupportTicketStatus, { label: string; cls: string; icon: React.ReactNode }> = {
    Open: { label: 'Open', cls: 'st-badge-open', icon: <Clock size={11} /> },
    InProgress: { label: 'In Progress', cls: 'st-badge-inprogress', icon: <RefreshCw size={11} /> },
    Resolved: { label: 'Resolved', cls: 'st-badge-resolved', icon: <CheckCircle2 size={11} /> },
    Closed: { label: 'Closed', cls: 'st-badge-closed', icon: <XCircle size={11} /> },
  }
  const { label, cls, icon } = map[status] ?? { label: status, cls: '', icon: null }
  return <span className={`st-badge ${cls}`}>{icon}{label}</span>
}

function priorityBadge(priority: SupportTicketPriority) {
  const map: Record<SupportTicketPriority, string> = {
    Low: 'st-pri-low',
    Medium: 'st-pri-medium',
    High: 'st-pri-high',
    Critical: 'st-pri-critical',
  }
  return <span className={`st-priority ${map[priority] ?? ''}`}>{priority}</span>
}

function categoryLabel(category: string) {
  const map: Record<string, string> = {
    Bug: '🐛 Bug',
    FeatureRequest: '✨ Feature Request',
    Question: '❓ Question',
    Improvement: '⚡ Improvement',
    Other: '💬 Other',
  }
  return map[category] ?? category
}

function formatDate(iso?: string) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString()
}

// ── Detail panel ─────────────────────────────────────────────────────────────

interface DetailPanelProps {
  ticketId: string
  onClose: () => void
}

function DetailPanel({ ticketId, onClose }: DetailPanelProps) {
  const queryClient = useQueryClient()
  const [reply, setReply] = useState('')
  const [newStatus, setNewStatus] = useState<SupportTicketStatus | ''>('')
  const [newPriority, setNewPriority] = useState<SupportTicketPriority | ''>('')

  const { data: ticket, isLoading } = useQuery<SupportTicketDto>({
    queryKey: ['support-ticket', ticketId],
    queryFn: () => fetchSupportTicket(ticketId),
  })

  const updateMutation = useMutation({
    mutationFn: (req: { status?: SupportTicketStatus; priority?: SupportTicketPriority; adminReply?: string }) =>
      updateSupportTicket(ticketId, req),
    onSuccess: (updated) => {
      queryClient.setQueryData(['support-ticket', ticketId], updated)
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] })
      setReply('')
      setNewStatus('')
      setNewPriority('')
    },
  })

  const handleUpdate = () => {
    const req: { status?: SupportTicketStatus; priority?: SupportTicketPriority; adminReply?: string } = {}
    if (newStatus) req.status = newStatus
    if (newPriority) req.priority = newPriority
    if (reply.trim()) req.adminReply = reply.trim()
    if (!req.status && !req.priority && !req.adminReply) return
    updateMutation.mutate(req)
  }

  const handleQuickStatus = (s: SupportTicketStatus) => {
    updateMutation.mutate({ status: s })
  }

  if (isLoading) return <div className="st-detail-loading">Loading…</div>
  if (!ticket) return <div className="st-detail-loading">Ticket not found.</div>

  return (
    <div className="st-detail">
      <div className="st-detail-header">
        <div className="st-detail-header-left">
          <span className="st-detail-num">{ticket.ticketNumber}</span>
          {statusBadge(ticket.status)}
          {priorityBadge(ticket.priority)}
        </div>
        <button className="st-detail-close" onClick={onClose} aria-label="Close">
          <X size={18} />
        </button>
      </div>

      <div className="st-detail-body">
        <h2 className="st-detail-title">{ticket.title}</h2>
        <div className="st-detail-meta">
          <span>{categoryLabel(ticket.category)}</span>
          <span>·</span>
          <span>Submitted by <strong>{ticket.submittedByDisplayName ?? ticket.submittedByUserId}</strong></span>
          {ticket.submittedByEmail && <span>({ticket.submittedByEmail})</span>}
          <span>·</span>
          <span>{formatDate(ticket.createdUtc)}</span>
        </div>

        <div className="st-detail-desc">
          <h4>Description</h4>
          <p>{ticket.description}</p>
        </div>

        {/* Quick status buttons */}
        <div className="st-detail-quick-status">
          <span className="st-section-label">Quick status:</span>
          {STATUS_OPTIONS.filter(s => s !== ticket.status).map(s => (
            <button
              key={s}
              className="st-quick-btn"
              onClick={() => handleQuickStatus(s)}
              disabled={updateMutation.isPending}
            >
              {s === 'Closed' ? 'Close ticket' : `Mark ${s}`}
            </button>
          ))}
        </div>

        {/* Comments / history */}
        <div className="st-comments">
          <h4 className="st-section-label">
            <MessageSquare size={14} /> Thread ({ticket.comments.length})
          </h4>
          {ticket.comments.length === 0 && (
            <p className="st-no-comments">No comments yet.</p>
          )}
          {ticket.comments.map(c => (
            <div key={c.id} className={`st-comment ${c.isAdminReply ? 'st-comment-admin' : ''}`}>
              <div className="st-comment-header">
                <strong>{c.authorDisplayName ?? c.authorUserId}</strong>
                {c.isAdminReply && <span className="st-comment-admin-tag">Support</span>}
                <span className="st-comment-date">{formatDate(c.createdUtc)}</span>
              </div>
              <p className="st-comment-body">{c.body}</p>
            </div>
          ))}
        </div>

        {/* Admin update panel */}
        <div className="st-detail-update">
          <h4 className="st-section-label">Update Ticket</h4>
          <div className="st-update-row">
            <div className="st-update-field">
              <label>Status</label>
              <select value={newStatus} onChange={e => setNewStatus(e.target.value as SupportTicketStatus | '')}>
                <option value="">— keep current —</option>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="st-update-field">
              <label>Priority</label>
              <select value={newPriority} onChange={e => setNewPriority(e.target.value as SupportTicketPriority | '')}>
                <option value="">— keep current —</option>
                {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          <div className="st-reply-area">
            <label>Admin Reply (optional)</label>
            <textarea
              className="st-reply-textarea"
              placeholder="Write a reply to the user…"
              rows={3}
              maxLength={4000}
              value={reply}
              onChange={e => setReply(e.target.value)}
            />
          </div>

          {updateMutation.isError && (
            <p className="st-error">Failed to update. Please try again.</p>
          )}

          <div className="st-update-actions">
            <button
              className="st-btn st-btn-primary"
              onClick={handleUpdate}
              disabled={updateMutation.isPending || (!newStatus && !newPriority && !reply.trim())}
            >
              {updateMutation.isPending ? 'Saving…' : (
                <><Send size={14} /> Save Update</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SupportTickets() {
  const [statusFilter, setStatusFilter] = useState<SupportTicketStatus | ''>('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [page, setPage] = useState(1)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['support-tickets', statusFilter, page],
    queryFn: () =>
      fetchSupportTickets({
        status: statusFilter || undefined,
        page,
        pageSize: 20,
      }),
  })

  const tickets = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = Math.ceil(total / 20)

  return (
    <div className="st-page">
      <div className="st-page-header">
        <div className="st-page-title">
          <LifeBuoy size={22} />
          <h1>Support Tickets</h1>
          {total > 0 && <span className="st-total-badge">{total}</span>}
        </div>
        <div className="st-filters">
          <select
            className="st-filter-select"
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value as SupportTicketStatus | ''); setPage(1) }}
          >
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button className="st-refresh-btn" onClick={() => refetch()} title="Refresh">
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      <div className="st-layout">
        {/* Ticket list */}
        <div className={`st-list-panel ${selectedId ? 'st-list-narrow' : ''}`}>
          {isLoading && <div className="st-loading">Loading tickets…</div>}
          {isError && (
            <div className="st-error-panel">
              <AlertTriangle size={16} /> Failed to load tickets.
            </div>
          )}
          {!isLoading && !isError && tickets.length === 0 && (
            <div className="st-empty">No tickets found.</div>
          )}
          {tickets.map(ticket => (
            <button
              key={ticket.id}
              className={`st-ticket-row ${selectedId === ticket.id ? 'selected' : ''}`}
              onClick={() => setSelectedId(ticket.id === selectedId ? null : ticket.id)}
            >
              <div className="st-row-main">
                <div className="st-row-top">
                  <span className="st-row-num">{ticket.ticketNumber}</span>
                  {statusBadge(ticket.status)}
                  {priorityBadge(ticket.priority)}
                </div>
                <div className="st-row-title">{ticket.title}</div>
                <div className="st-row-meta">
                  <span>{categoryLabel(ticket.category)}</span>
                  <span>·</span>
                  <span>{ticket.submittedByDisplayName ?? ticket.submittedByUserId}</span>
                  <span>·</span>
                  <span>{formatDate(ticket.createdUtc)}</span>
                  {ticket.comments.length > 0 && (
                    <>
                      <span>·</span>
                      <span><MessageSquare size={11} /> {ticket.comments.length}</span>
                    </>
                  )}
                </div>
              </div>
              <ChevronRight size={16} className="st-row-chevron" />
            </button>
          ))}

          {totalPages > 1 && (
            <div className="st-pagination">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
              <span>Page {page} of {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
            </div>
          )}
        </div>

        {/* Detail panel */}
        {selectedId && (
          <div className="st-detail-panel">
            <DetailPanel ticketId={selectedId} onClose={() => setSelectedId(null)} />
          </div>
        )}
      </div>
    </div>
  )
}
